import { FC, createContext, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import CreateEditJobHeader from './components/CreateEditJobHeader';
import CreateEditJobNav from './components/CreateEditJobSideNav';
import { CreateEditJobNavEnum, CreateJobErrorTypes, JOB_ATTRIBUTE_TYPE } from '../constant';
import NotesAndAttachmentsSection from './components/NotesAndAttachmentsSection';
import MoreOptionsSection from './components/MoreOptionsSection';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  useAutoFillJobDetailMutation,
  useCheckJobReferenceNumberMutation,
  useCreateJobFromInvoiceMutation,
  useCreateJobMutation,
  useEditJobMutation,
  useLazyGetJobByIdQuery
} from '../api';
import {
  selectCurrentTeam,
  selectIsActionsBlocked,
  selectMerchantCustomizations,
  showNotifier
} from 'containers/app/appSlice';

import JobCustomer from './components/JobCustomer';
import JobDetailsSection from './components/JobDetailsSection';
import ScheduleAndAssignTo from './components/ScheduleAndAssignTo';
import JobLineItemSection from './components/JobLineItemSection';
import {
  checkIfJobValuesAreValid,
  getModifiedAutoFilledFields,
  hasChangesToSyncInJob,
  mapRiskValidationEntityToErrorType,
  prepareCreateJobPayload
} from 'containers/jobs/utils';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import routesPath from 'routes/RoutesPath';
import { useLazyGetCustomerByIdQuery, useLazyGetInvoiceByIdV2Query } from 'containers/invoices/api';
import {
  addJobValidationError,
  resetAutoFilledData,
  resetCreateJobState,
  resetJobValidationErrors,
  selectAutoFilledData,
  selectAutoFilledDataCount,
  selectEstimateNumberFromJob,
  selectEstimateStatus,
  selectInitialJobReferenceNumber,
  selectInvoiceNumberForJobFromInvoice,
  selectIsFromEstimate,
  selectIsInitialisingJobFromAPI,
  selectIsJobFromInvoice,
  selectJobInvoiceStatus,
  selectJobReferenceNumber,
  updateJobCustomer,
  updateServiceAddress
} from './createJobSlice';
import JobCreationConfirmationPopup from './components/JobCreationConfirmationPopup';
import { GenericModal, Loader } from 'components';
import { NotifierTypes } from 'constants/notifierConstants';
import DateUtils from 'utils/dateUtils';
import { InvoiceValidationError } from 'containers/invoices/create-invoice/types';
import type { CreateJobResponse, UpdateJobResponse } from '../jobs.model';
import { InvoiceStatus } from 'containers/invoices/invoices.model';
import { CrossIconvector } from 'assets/icons';
import { logAnalyticEvent } from 'utils/analytics';
import { JobsCleverTapEvents } from '../events';
import { throttle } from 'utils/commonUtils';
import { useLazyGetEstimateByIdQuery } from 'containers/estimates/api';
import { EstimateStatus } from 'containers/estimates/constants';
import BlockedInfoPopup from 'containers/home/components/BlockedInfoPopup';
import AutoFillJobDetailsModal from './components/AutoFillJobDetailsModal';
import type { AutoFillAttachment } from 'components/auto-fill-generic';
import { AutoFillFeedbackBanner, AutoFillFeedbackModal } from 'components/auto-fill-generic';
import { API_SOURCE, ENTITY_TYPE } from 'constants/common';
import { showNotification } from 'utils/Notifier';
import { useSubmitAutoFillFeedbackMutation } from 'containers/invoices/api';

interface ICreateJobContext {
  selectedJobTab: CreateEditJobNavEnum;
  setSelectedJobTab: React.Dispatch<React.SetStateAction<CreateEditJobNavEnum>>;
}

interface CreateJobProps {
  isEdit?: boolean;
}

const CreateJobContext = createContext<ICreateJobContext>({
  selectedJobTab: CreateEditJobNavEnum.JOB_FOR,
  setSelectedJobTab: null
});

const CreateJob: FC<CreateJobProps> = ({ isEdit = false }) => {
  const [selectedJobTab, setSelectedJobTab] = useState(CreateEditJobNavEnum.JOB_FOR);
  const [showJobSaveConfirmationPopup, setShowJobSaveConfirmationPopup] = useState(false);
  const [showSyncConfirmationPopup, setShowSyncConfirmationPopup] = useState(false);
  const [showMerchantBlockedPopup, setShowMerchantBlockedPopup] = useState(false);
  const [showAutoFillModal, setShowAutoFillModal] = useState(false);
  const [showAutoFillFeedbackModal, setShowAutoFillFeedbackModal] = useState(false);
  const [showAutoFillFeedbackBanner, setShowAutoFillFeedbackBanner] = useState(false);

  const currentTeam = useAppSelector(selectCurrentTeam);
  const isActionsBlocked = useAppSelector(selectIsActionsBlocked);
  const selectedJobReferenceNumber = useAppSelector(selectJobReferenceNumber);
  const initialJobReferenceNumber = useAppSelector(selectInitialJobReferenceNumber);
  const isInitialisingSliceData = useAppSelector(selectIsInitialisingJobFromAPI);
  const isFromInvoice = useAppSelector(selectIsJobFromInvoice);
  const invoiceNumber = useAppSelector(selectInvoiceNumberForJobFromInvoice);
  const invoiceStatus = useAppSelector(selectJobInvoiceStatus);
  const isFromEstimate = useAppSelector(selectIsFromEstimate);
  const estimateStatus = useAppSelector(selectEstimateStatus);
  const estimateNumber = useAppSelector(selectEstimateNumberFromJob);
  const autoFilledData = useAppSelector(selectAutoFilledData);
  const autoFilledDataCount = useAppSelector(selectAutoFilledDataCount);
  const merchantCustomisations = useAppSelector(selectMerchantCustomizations);
  const jobAutofillEnabled = merchantCustomisations?.teamFeatures?.jobAutofillEnabled;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isDuplicate = location.pathname.includes('/duplicate');

  const [
    createJobMutation,
    { isSuccess: isJobCreationSuccess, isLoading: isCreateJobInProgress, data: createJobResponse }
  ] = useCreateJobMutation();
  const [editJobMutation, { isSuccess: isEditJobSuccess, isLoading: isEditJobInProgress, data: editJobResponse }] =
    useEditJobMutation();
  const [
    createJobFromInvoiceMutation,
    {
      isSuccess: isCreateJobFromInvoiceSuccess,
      isLoading: isCreateJobFromInvoiceInProgress,
      data: createJobFromInvoiceResponse
    }
  ] = useCreateJobFromInvoiceMutation();
  const [getCustomerByIdQuery, getCustomerByIdState] = useLazyGetCustomerByIdQuery();
  const { data: customerData, isSuccess: customerDataSuccess } = getCustomerByIdState;
  const [checkJobRefNumberQuery, { data: jobReferenceNumberCheckResponse }] = useCheckJobReferenceNumberMutation();
  const [getJobByIdQuery, { isFetching: isFetchingJob, isUninitialized: isUninitializedJob }] =
    useLazyGetJobByIdQuery();
  const [getEstimateByIdQuery, { isFetching: isFetchingEstimate }] = useLazyGetEstimateByIdQuery();
  const [getInvoiceByIdQuery, { isFetching: isFetchingInvoice }] = useLazyGetInvoiceByIdV2Query();
  const [autoFillJobDetail, { isLoading: isAutoFillJobDetailInProgress, isSuccess: isAutoFillJobDetailSuccess }] =
    useAutoFillJobDetailMutation();
  const [submitAutoFillFeedback, { isLoading: isSubmitJobFeedbackLoading }] = useSubmitAutoFillFeedbackMutation();

  const queryParameters = new URLSearchParams(window.location.search);
  const invoiceId = queryParameters.get('invoiceId');
  const estimateId = queryParameters.get('estimateId');
  const pathParams = useParams();
  const { jobId } = pathParams;

  const isPaidInvoice =
    invoiceStatus === InvoiceStatus.PAID ||
    invoiceStatus === InvoiceStatus.PAID_QR ||
    invoiceStatus === InvoiceStatus.REFUNDED ||
    invoiceStatus === InvoiceStatus.REFUND_INITIATED;

  const isPendingEstimate = estimateStatus?.toUpperCase() === EstimateStatus.PENDING;

  useEffect(() => {
    if (!isEdit) return;
    const lineItemsTab = location.state?.selectedJobTab;
    if (lineItemsTab) {
      setSelectedJobTab(lineItemsTab);
    }
  }, [location.state?.selectedJobTab]);

  useEffect(() => {
    const customerId = queryParameters.get('customerId');
    if (customerId) {
      getCustomerByIdQuery({ teamId: currentTeam.id, customerId: customerId as string });
    }
    if (invoiceId) {
      getInvoiceByIdQuery({
        invoiceId: invoiceId,
        teamId: currentTeam.id,
        isFromJob: true
      });
    }
    if (estimateId) {
      getEstimateByIdQuery({ teamId: currentTeam.id, estimateId: estimateId as string, isFromJob: true });
    }
    dispatch(resetJobValidationErrors());
    if (isEdit) {
      logAnalyticEvent(JobsCleverTapEvents.webJobsEditPageView);
    } else {
      logAnalyticEvent(JobsCleverTapEvents.webJobsCreatePageView);
    }
  }, []);

  useEffect(() => {
    if (isFromInvoice && !isInitialisingSliceData && !isEdit) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: 'Customer and invoice details copied to job',
            isMessageHtml: false
          },
          showClose: true,
          type: NotifierTypes.SUCCESS
        })
      );
    }
  }, [isInitialisingSliceData, isFromInvoice]);

  useEffect(() => {
    if (jobId) {
      getJobByIdQuery({ teamId: currentTeam.id, jobId, isFromEdit: !isDuplicate, isDuplicateJob: isDuplicate });
    }
  }, [jobId]);

  useEffect(() => {
    if (customerDataSuccess) {
      dispatch(updateJobCustomer({ ...customerData, ID: customerData?.id }));
      if (customerData?.serviceAddress?.length > 0) dispatch(updateServiceAddress(customerData.serviceAddress[0]));
    }
  }, [customerDataSuccess]);

  const scrollToSelectedTab = useCallback(() => {
    const scrollContainer = document.getElementById('scrollable-job-div');
    if (!scrollContainer) return;

    const tabElem = document.getElementById(selectedJobTab);
    if (!tabElem) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const tabRect = tabElem.getBoundingClientRect();
    const relativeTop = tabRect.top - containerRect.top;

    scrollContainer.scrollTo({
      top: scrollContainer.scrollTop + relativeTop - 20,
      behavior: 'smooth'
    });
  }, [selectedJobTab]);

  useLayoutEffect(() => {
    if (!isInitialisingSliceData && !isFetchingJob && !isFetchingInvoice && !isFetchingEstimate) {
      requestAnimationFrame(() => {
        scrollToSelectedTab();
      });
    }
  }, [
    selectedJobTab,
    scrollToSelectedTab,
    isInitialisingSliceData,
    isFetchingJob,
    isFetchingInvoice,
    isFetchingEstimate
  ]);

  useEffect(() => {
    if (!isJobCreationSuccess || !createJobResponse) return;

    const response = createJobResponse as CreateJobResponse & {
      isRiskValidationFailed?: boolean;
      riskValidations?: CreateJobResponse['data']['riskValidations'];
    };
    const data = response.data ?? response;
    const payload = data as CreateJobResponse['data'];
    const riskErrors = payload.riskValidations?.errors ?? response.riskValidations?.errors ?? [];
    const isRiskFailed =
      Boolean(payload.isRiskValidationFailed ?? response.isRiskValidationFailed) && riskErrors.length > 0;

    if (isRiskFailed && riskErrors.length > 0) {
      dispatch(resetJobValidationErrors());
      const errors: InvoiceValidationError[] = [];
      riskErrors.forEach((err: { entity: string; message: string }) => {
        const type = mapRiskValidationEntityToErrorType(err.entity);
        dispatch(addJobValidationError({ type, errorMsg: err.message }));
        errors.push({ type, errorMsg: err.message });
      });
      handleErrors(errors);
      return;
    }

    if (autoFilledData) {
      const modifiedFields = getModifiedAutoFilledFields();
      logAnalyticEvent(JobsCleverTapEvents.webJobCreatedAfterAutofill, {
        autofillFieldEdited: modifiedFields?.length > 0,
        modifiedFields: modifiedFields
      });
    } else {
      logAnalyticEvent(JobsCleverTapEvents.webJobCreatedManually);
    }
    showNotification(dispatch, NotifierTypes.SUCCESS, 'Job created successfully!');
    navigate(routesPath.JOBS, { replace: true });
    dispatch(resetCreateJobState());
    dispatch(resetAutoFilledData());
  }, [isJobCreationSuccess, createJobResponse]);

  useEffect(() => {
    if (isCreateJobFromInvoiceSuccess && createJobFromInvoiceResponse) {
      setShowSyncConfirmationPopup(false);
      const response = createJobFromInvoiceResponse as CreateJobResponse & {
        isRiskValidationFailed?: boolean;
        riskValidations?: CreateJobResponse['data']['riskValidations'];
      };
      const data = response.data ?? response;
      const payload = data as CreateJobResponse['data'];
      const riskErrors = payload.riskValidations?.errors ?? response.riskValidations?.errors ?? [];
      const isRiskFailed =
        Boolean(payload.isRiskValidationFailed ?? response.isRiskValidationFailed) && riskErrors.length > 0;

      if (isRiskFailed && riskErrors.length > 0) {
        dispatch(resetJobValidationErrors());
        const errors: InvoiceValidationError[] = [];
        riskErrors.forEach((err: { entity: string; message: string }) => {
          const type = mapRiskValidationEntityToErrorType(err.entity);
          dispatch(addJobValidationError({ type, errorMsg: err.message }));
          errors.push({ type, errorMsg: err.message });
        });
        handleErrors(errors);
        return;
      }
      showNotification(dispatch, NotifierTypes.SUCCESS, 'Job created successfully!');
      navigate(-1);
      dispatch(resetCreateJobState());
      return;
    }
    if (!isEditJobSuccess || !editJobResponse) return;

    const response = editJobResponse as UpdateJobResponse & {
      isRiskValidationFailed?: boolean;
      riskValidations?: UpdateJobResponse['data']['riskValidations'];
    };
    const data = response.data ?? response;
    const payload = data as UpdateJobResponse['data'];
    const riskErrors = payload.riskValidations?.errors ?? response.riskValidations?.errors ?? [];
    const isRiskFailed =
      Boolean(payload.isRiskValidationFailed ?? response.isRiskValidationFailed) && riskErrors.length > 0;

    if (isRiskFailed && riskErrors.length > 0) {
      dispatch(resetJobValidationErrors());
      const errors: InvoiceValidationError[] = [];
      riskErrors.forEach((err: { entity: string; message: string }) => {
        const type = mapRiskValidationEntityToErrorType(err.entity);
        dispatch(addJobValidationError({ type, errorMsg: err.message }));
        errors.push({ type, errorMsg: err.message });
      });
      handleErrors(errors);
      return;
    }

    showNotification(dispatch, NotifierTypes.SUCCESS, 'Job edited successfully!');
    navigate(-1);
    dispatch(resetCreateJobState());
  }, [isEditJobSuccess, isCreateJobFromInvoiceSuccess, createJobFromInvoiceResponse, editJobResponse]);

  useEffect(() => {
    if (isFromEstimate && !isInitialisingSliceData && !isEdit) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: 'Customer and estimate details copied to job',
            isMessageHtml: false
          },
          showClose: true,
          type: NotifierTypes.SUCCESS
        })
      );
    }
  }, [isFromEstimate, isInitialisingSliceData, isEdit]);

  const handleErrors = (errors: InvoiceValidationError[]) => {
    if (!errors) return;

    const validationMap = {
      [CreateEditJobNavEnum.JOB_FOR]: [CreateJobErrorTypes.CUSTOMER, CreateJobErrorTypes.SERVICE_ADDRESS],
      [CreateEditJobNavEnum.JOB_DETAILS]: [CreateJobErrorTypes.LEAD_SOURCE, CreateJobErrorTypes.JOB_TITLE],
      [CreateEditJobNavEnum.JOB_SCHEDULE]: [CreateJobErrorTypes.SCHEDULE_DATE, CreateJobErrorTypes.ASSIGN_MEMBER],
      [CreateEditJobNavEnum.LINE_ITEMS]: [CreateJobErrorTypes.LINE_ITEMS]
    };

    const tabKey = Object.keys(validationMap).find(key => {
      return errors.some(validation => validationMap[key].includes(validation.type));
    });

    const tabElem = tabKey ? document.getElementById(tabKey) : null;
    tabElem?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
  };

  const checkJobReferenceNumber = value => {
    const jobRefNumberResult = checkJobRefNumberQuery({
      teamId: currentTeam.id,
      body: { value, attribute: JOB_ATTRIBUTE_TYPE.REFERENCE_ID }
    }).unwrap();
    return jobRefNumberResult;
  };

  const onCreateJob = async () => {
    if (isActionsBlocked) {
      setShowMerchantBlockedPopup(true);
      return;
    }
    const { isValid, errors } = checkIfJobValuesAreValid();
    if (!isValid) {
      handleErrors(errors);
      return;
    }
    const jobRefNumber = selectedJobReferenceNumber.trim();
    if (jobRefNumber !== '' && jobRefNumber !== initialJobReferenceNumber.trim()) {
      const { data } = await checkJobReferenceNumber(jobRefNumber);
      const { isExists } = data ?? {};
      if (isExists) {
        setShowJobSaveConfirmationPopup(true);
        return;
      }
    }
    const hasChangesToSync = hasChangesToSyncInJob();
    const showSyncConfirmation =
      (isFromInvoice && !isPaidInvoice && hasChangesToSync) ||
      (isFromEstimate && isPendingEstimate && hasChangesToSync);

    if (isEdit) {
      if (showSyncConfirmation) {
        setShowSyncConfirmationPopup(true);
      } else {
        handleEditJob();
      }
      return;
    }

    if (isFromInvoice) {
      if (isPaidInvoice || !hasChangesToSync) {
        handleCreateJobFromInvoice();
      } else {
        setShowSyncConfirmationPopup(true);
      }
      return;
    }

    if (isFromEstimate && isPendingEstimate && hasChangesToSync) {
      setShowSyncConfirmationPopup(true);
      return;
    }
    handleCreateJob();
  };

  const onContinueWithSameReferenceNumber = () => {
    const hasChangesToSync = hasChangesToSyncInJob();
    const showSyncConfirmation =
      (isFromInvoice || isFromEstimate) && !isPaidInvoice && isPendingEstimate && hasChangesToSync;
    if (isEdit) {
      if (showSyncConfirmation) {
        setShowSyncConfirmationPopup(true);
        return;
      }
      handleEditJob();
      return;
    }

    if (isFromInvoice) {
      if (isPaidInvoice || !hasChangesToSyncInJob()) {
        handleCreateJobFromInvoice();
      } else {
        setShowSyncConfirmationPopup(true);
      }
      return;
    }

    if (isFromEstimate && isPendingEstimate && hasChangesToSync) {
      setShowSyncConfirmationPopup(true);
      return;
    }
    handleCreateJob();
  };

  const handleCreateJob = () => {
    createJobMutation({
      teamId: currentTeam.id.toString(),
      body: prepareCreateJobPayload({ isAutoFilled: Boolean(autoFilledData) })
    });
  };

  const handleCreateJobFromInvoice = () => {
    createJobFromInvoiceMutation({
      teamId: currentTeam.id.toString(),
      invoiceId: invoiceId,
      body: { ...prepareCreateJobPayload({}), invoiceNumber: invoiceNumber }
    });
  };

  const handleEditJob = () => {
    editJobMutation({ teamId: currentTeam.id.toString(), jobId, body: prepareCreateJobPayload({ isFromEdit: true }) });
  };

  const handleCreateEditJobFromOtherEntities = () => {
    if (isEdit) {
      handleEditJob();
      return;
    }
    if (isFromInvoice) {
      handleCreateJobFromInvoice();
      return;
    }
    handleCreateJob();
  };

  const handleCreateJobConfirmationClose = () => {
    setShowJobSaveConfirmationPopup(false);
  };

  const createJobHandler = useCallback(throttle(onCreateJob, 800), [
    isEdit,
    isPaidInvoice,
    isFromInvoice,
    isFromEstimate,
    isActionsBlocked,
    autoFilledData
  ]);

  const getSyncPopupTitleAndSubtitle = (type: 'title' | 'subtitle') => {
    const hasLinkedInvoice = Boolean(invoiceNumber && !isPaidInvoice);
    const hasLinkedEstimate = Boolean(isPendingEstimate);
    const linkedEntityLabels = [hasLinkedInvoice && 'invoice', hasLinkedEstimate && 'estimate'].filter(Boolean);
    const linkedEntityRefs = [hasLinkedInvoice && invoiceNumber, hasLinkedEstimate && estimateNumber].filter(Boolean);
    const linkedEntitiesText = linkedEntityLabels.join(' and ');
    const linkedRefsText = linkedEntityRefs.join(' and ');
    const title = `Save and sync changes to linked ${linkedEntitiesText}?`;
    const subtitle = `Changes will be applied to \n${linkedRefsText} as well.`;
    return type === 'title' ? title : subtitle;
  };

  const handleAutoFillJobSave = async (content: string, attachments: AutoFillAttachment[]) => {
    const timeZone = DateUtils.getTimezoneLongName();
    await autoFillJobDetail({
      teamId: currentTeam.id.toString(),
      body: {
        content,
        timeZone,
        attachments,
        source: API_SOURCE.WEB
      }
    })
      .unwrap()
      .then(() => {
        setShowAutoFillModal(false);
        dispatch(resetJobValidationErrors());
      });
  };

  useEffect(() => {
    if (isAutoFillJobDetailSuccess && autoFilledData?.autofillKey) {
      setShowAutoFillFeedbackBanner(true);
    }
  }, [isAutoFillJobDetailSuccess, autoFilledData?.autofillKey]);

  const thankYouToast = useCallback(() => {
    showNotification(
      dispatch,
      NotifierTypes.SUCCESS,
      'Thank you for your feedback.',
      4000,
      'autofill-feedback-success'
    );
  }, [dispatch]);

  const handleFeedbackLike = useCallback(async () => {
    if (isSubmitJobFeedbackLoading || !autoFilledData?.autofillKey) {
      return;
    }
    try {
      await submitAutoFillFeedback({
        teamId: currentTeam.id.toString(),
        body: {
          autofillKey: autoFilledData.autofillKey,
          isMerchantApproved: true,
          entityType: ENTITY_TYPE.JOB
        }
      }).unwrap();

      thankYouToast();
      setShowAutoFillFeedbackBanner(false);
    } catch (error) {
      showNotification(dispatch, NotifierTypes.ERROR, 'Failed to submit feedback. Please try again.');
    }
  }, [
    submitAutoFillFeedback,
    currentTeam.id,
    autoFilledData?.autofillKey,
    dispatch,
    isSubmitJobFeedbackLoading,
    thankYouToast
  ]);

  const handleFeedbackDislike = useCallback(() => {
    setShowAutoFillFeedbackModal(true);
  }, []);

  const handleSendFeedback = useCallback(
    async (reason: string, comment: string) => {
      if (!autoFilledData?.autofillKey) {
        return;
      }
      try {
        await submitAutoFillFeedback({
          teamId: currentTeam.id.toString(),
          body: {
            autofillKey: autoFilledData.autofillKey,
            isMerchantApproved: false,
            merchantRejectedReason: reason,
            merchantRejectedComment: comment,
            entityType: ENTITY_TYPE.JOB
          }
        }).unwrap();

        thankYouToast();
        setShowAutoFillFeedbackModal(false);
        setShowAutoFillFeedbackBanner(false);
      } catch (error) {
        showNotification(dispatch, NotifierTypes.ERROR, 'Failed to submit feedback. Please try again.');
      }
    },
    [submitAutoFillFeedback, currentTeam.id, autoFilledData?.autofillKey, dispatch, thankYouToast]
  );

  if (isInitialisingSliceData || (isEdit && isUninitializedJob)) {
    return <Loader />;
  }

  return (
    <>
      <CreateJobContext.Provider value={{ selectedJobTab, setSelectedJobTab }}>
        <div className="relative flex h-full w-full flex-col">
          <CreateEditJobHeader
            isEdit={isEdit}
            onCreate={createJobHandler}
            onAutoFill={jobAutofillEnabled ? () => setShowAutoFillModal(true) : undefined}
            isActionInProgress={isCreateJobInProgress || isEditJobInProgress || isCreateJobFromInvoiceInProgress}
          />
          <div className="flex h-full w-full flex-row gap-x-[120px] overflow-hidden bg-primary">
            <div
              className="hidden w-1/3 max-w-[31.25rem] flex-col items-center p-11
            pr-0 lg:flex lg:w-80">
              <CreateEditJobNav context={CreateJobContext} />
            </div>
            <div
              id="scrollable-job-div"
              className="customNormalScroll flex grow flex-col space-y-8 overflow-y-auto p-12 pl-0">
              <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditJobNavEnum.JOB_FOR}>
                {showAutoFillFeedbackBanner && (
                  <AutoFillFeedbackBanner
                    autoFilledFieldsCount={autoFilledDataCount}
                    onStartNewAutoFill={() => setShowAutoFillModal(true)}
                    onFeedbackLike={handleFeedbackLike}
                    onFeedbackDislike={handleFeedbackDislike}
                    isLoading={isSubmitJobFeedbackLoading}
                    subText="Please review the highlighted items before creating the job."
                  />
                )}
                <JobCustomer context={CreateJobContext} />
              </div>
              <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditJobNavEnum.JOB_DETAILS}>
                <JobDetailsSection context={CreateJobContext} />
              </div>
              <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditJobNavEnum.JOB_SCHEDULE}>
                <ScheduleAndAssignTo context={CreateJobContext} isEdit={isEdit} />
              </div>
              <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditJobNavEnum.LINE_ITEMS}>
                <JobLineItemSection context={CreateJobContext} isLoadingTaxesAndFees={false} />
              </div>

              <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditJobNavEnum.NOTES_ATTACHMENTS}>
                <NotesAndAttachmentsSection context={CreateJobContext} />
              </div>
              <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditJobNavEnum.MORE_OPTIONS}>
                <MoreOptionsSection context={CreateJobContext} />
              </div>
            </div>
          </div>
        </div>
      </CreateJobContext.Provider>
      {showJobSaveConfirmationPopup && (
        <JobCreationConfirmationPopup
          isSaveItemLoading={isCreateJobInProgress || isEditJobInProgress || isCreateJobFromInvoiceInProgress}
          onContinue={onContinueWithSameReferenceNumber}
          closeSaveConfirmation={handleCreateJobConfirmationClose}
          jobRefNumber={selectedJobReferenceNumber}
          conflictingItems={jobReferenceNumberCheckResponse?.data?.jobNumberList}
        />
      )}
      {showSyncConfirmationPopup && (
        <GenericModal
          showModal={true}
          title={getSyncPopupTitleAndSubtitle('title')}
          titleStyle="text-center"
          subTitleStyle="text-center text-primaryText text-sbase font-normal"
          subTitle={getSyncPopupTitleAndSubtitle('subtitle')}
          primaryBtnLabel="Yes, continue"
          primaryBtnId="sync-job-invoice"
          primaryBtnStyle="mt-2"
          onPrimaryBtnClick={handleCreateEditJobFromOtherEntities}
          secondaryBtnId="cancel-sync-job-invoice"
          secondaryBtnStyle="border-none shadow-none hover:bg-transparent"
          secondaryBtnLabel="Cancel"
          onSecondaryBtnClick={() => setShowSyncConfirmationPopup(false)}
          closeIconStyle="top-3 right-3"
          CloseIcon={() => (
            <CrossIconvector
              height={18}
              width={18}
              title="sync-popup-close"
              className="cursor-pointer"
              onClick={() => setShowSyncConfirmationPopup(false)}
            />
          )}
          isActionInProgress={isCreateJobInProgress || isEditJobInProgress || isCreateJobFromInvoiceInProgress}
          height="min-h-[262px]"
          className="!px-5 !py-7 !pb-5"
        />
      )}
      {showMerchantBlockedPopup && (
        <BlockedInfoPopup isOpen={showMerchantBlockedPopup} onClose={() => setShowMerchantBlockedPopup(false)} />
      )}
      <AutoFillJobDetailsModal
        isOpen={showAutoFillModal}
        onClose={() => setShowAutoFillModal(false)}
        onSave={handleAutoFillJobSave}
        isSaving={isAutoFillJobDetailInProgress}
      />
      {showAutoFillFeedbackModal && (
        <AutoFillFeedbackModal
          isOpen={showAutoFillFeedbackModal}
          onClose={() => setShowAutoFillFeedbackModal(false)}
          onSendFeedback={handleSendFeedback}
          isLoading={isSubmitJobFeedbackLoading}
        />
      )}
    </>
  );
};

export default CreateJob;
